import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Users, CreditCard, BarChart, Activity } from "lucide-react";
import AnimatedStatCard from "../components/dashboard/AnimatedStatCard";
import AnimatedActivityList from "../components/dashboard/AnimatedActivityList";
import QuickActions from "../components/dashboard/QuickActions";
import {
  AnimatedRevenueChart,
  AnimatedUserGrowthChart,
  AnimatedUserTypeChart,
} from "../components/dashboard/AnimatedCharts";
import { useDashboard } from "../hooks/useDashboard";
import { useRole } from "../contexts/RoleContext";
import { hasPermissionToActionButton } from "../lib/utils";
import { useLocation } from "react-router-dom";

function AnimatedDashboard() {
  const { logout } = useAuth();
  const { fetchDashboard } = useDashboard();
  const { role, loading } = useRole();
  const location = useLocation();
  const pathname = location.pathname;
  const permission = hasPermissionToActionButton(role, pathname);

  const {
    data: dashboardResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["dashboard"],
    queryFn: fetchDashboard,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Use fetched data or fallback to empty/defaults
  // const recentActivity = dashboardData?.recentActivity || [];
  // const stats = dashboardData?.stats || {};
  // const revenue = dashboardData?.revenue || {};
  // const userGrowth = dashboardData?.userGrowth || {};
  // const userType = dashboardData?.userType || {};
  const dashboardData = dashboardResponse?.data ?? {};
  const totalUsers        = dashboardData.totalUserCount     ?? 0;
  const subscribedUsers   = dashboardData.subscribedUserCount ?? 0;
  const monthlyNewUsers   = dashboardData.monthlyNewUserCounts;
  const userTypes         = dashboardData.userTypeCounts     ?? {};
  const conversionRate    = dashboardData.conversionChange ?? 0;
  const revenueDataChart       = dashboardData.monthlyRevenueCounts ?? {};
  const revenue = dashboardData.monthlyRevenue ?? 0;
// Recent activity data
  const recentActivity = [
    {
      id: 1,
      title: "New Premium Subscription",
      description: "User John Doe upgraded to Premium plan",
      time: "15 minutes ago",
      icon: <CreditCard className="h-4 w-4 text-primary" />,
    },
    {
      id: 2,
      title: "New User Registration",
      description: "Amy Johnson created a new account",
      time: "1 hour ago",
      icon: <Users className="h-4 w-4 text-green-500" />,
    },
    {
      id: 3,
      title: "Voice Command Added",
      description: 'New command "Play sleep music" was added',
      time: "3 hours ago",
      icon: <BarChart className="h-4 w-4 text-blue-500" />,
    },
    {
      id: 4,
      title: "Pet Store Purchase",
      description: "User Mark Wilson purchased new pet accessories",
      time: "5 hours ago",
      icon: <CreditCard className="h-4 w-4 text-purple-500" />,
    },
    {
      id: 5,
      title: "System Update",
      description: "System successfully updated to version 2.4.0",
      time: "1 day ago",
      icon: <BarChart className="h-4 w-4 text-yellow-500" />,
    },
  ];
  // Page animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const childVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div>
      {permission && permission.view && (
        <motion.main
          className=""
          variants={pageVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div className="mb-6" variants={childVariants}>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-gray-600">
              Welcome back, Admin! Here's what's happening.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6"
            variants={childVariants}
          >
            <AnimatedStatCard
              title="Total Users"
              value={totalUsers.count || "0"}
              icon={<Users className="h-6 w-6 text-primary" />}
              change={totalUsers.change || ""}
              changeType={totalUsers.changeType || "increase"}
              description="vs. last month"
              delay={0}
            />

            <AnimatedStatCard
              title="Premium Users"
              value={subscribedUsers.count || "0"}
              icon={<Users className="h-6 w-6 text-green-500" />}
              change={subscribedUsers.change || ""}
              changeType={subscribedUsers.changeType || "increase"}
              description="vs. last month"
              delay={1}
            />
            
            <AnimatedStatCard 
              title="Monthly Revenue" 
              value={ revenue?.count || "0" } 
              icon={<CreditCard className="h-6 w-6 text-blue-500" />} 
              change={ revenue?.change || "" } 
              changeType={ revenue?.changeType || "increase" }
              description="vs. last month"
              delay={2}
            />
            
            <AnimatedStatCard 
              title="Conversion Rate" 
              value={ conversionRate?.conversionRate || "0%"} 
              icon={<Activity className="h-6 w-6 text-purple-500" />} 
              change={ conversionRate?.change || "" } 
              changeType={ conversionRate?.changeType || "increase" } 
              description="vs. last month"
              delay={3}
            />
          </motion.div>

          {/* Quick Actions */}
          <QuickActions />

          {/* Charts & Activity Section */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6"
            variants={childVariants}
          >
            {/* Revenue Chart */}
            <AnimatedRevenueChart data={revenueDataChart} delay={0.3} />
            
            {/* Recent Activity */}
            {/* <motion.div 
              className="bg-white rounded-lg shadow-sm p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            > */}
              {/* <AnimatedActivityList activities={recentActivity} /> */}
            </motion.div>
          {/* </motion.div> */}
          
          {/* Additional Charts */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={childVariants}
          >
            {/* User Growth Chart */}
            <AnimatedUserGrowthChart data={monthlyNewUsers} delay={0.5} />

            {/* User Types Chart */}
            <AnimatedUserTypeChart data={dashboardData} delay={0.6} />
          </motion.div>
        </motion.main>
      )}
    </div>
  );
}

export default AnimatedDashboard;
